import { Copy, ExternalLink, Link2, PlusCircle } from "lucide-react";
import { useState, type FormEvent } from "react";
import type { Currency } from "../domain/types";
import {
  Button,
  EmptyState,
  Field,
  Modal,
  PageHeader,
  StatusChip,
} from "../components/UI";
import { PlatformBadge } from "../components/PlatformBadge";
import { validateAffiliateLink } from "../lib/affiliateLinks";
import { money } from "../lib/format";
import { useWeSell } from "../state/WeSellProvider";

export function Links() {
  const { data, saving, addPromotion } = useWeSell();
  const [copied, setCopied] = useState("");
  const [open, setOpen] = useState(false);
  const [productId, setProductId] = useState("");
  const [contentId, setContentId] = useState("");
  const [url, setUrl] = useState("");
  const [trackingId, setTrackingId] = useState("");
  const [platformFilter, setPlatformFilter] = useState("all");
  const [contentFilter, setContentFilter] = useState("all");
  if (!data) return null;

  const selectedProduct = data.products.find((item) => item.id === productId);
  const selectedPlatform = data.platforms.find(
    (item) => item.id === selectedProduct?.platformId,
  );
  const validation = selectedPlatform
    ? validateAffiliateLink(selectedPlatform, url, trackingId)
    : null;
  const filtered = data.promotions.filter(
    (item) =>
      (platformFilter === "all" || item.platformId === platformFilter) &&
      (contentFilter === "all" || item.contentId === contentFilter),
  );

  const commissionByPromotion = (() => {
    const values = new Map<string, Map<Currency, number>>();
    for (const sale of data.sales) {
      if (!sale.promotionId || sale.status !== "approved") continue;
      const currencies =
        values.get(sale.promotionId) ?? new Map<Currency, number>();
      currencies.set(
        sale.currency,
        (currencies.get(sale.currency) ?? 0) + sale.commissionAmount,
      );
      values.set(sale.promotionId, currencies);
    }
    return values;
  })();

  const copy = async (value: string, id: string) => {
    await navigator.clipboard.writeText(value);
    setCopied(id);
    window.setTimeout(() => setCopied(""), 1600);
  };
  const showCreate = () => {
    const first = data.products[0];
    setProductId(first?.id ?? "");
    setContentId("");
    setUrl(first?.affiliateUrl ?? "");
    setTrackingId("");
    setOpen(true);
  };
  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!selectedProduct || !selectedPlatform) return;
    await addPromotion({
      platformId: selectedPlatform.id,
      productId: selectedProduct.id,
      contentId: contentId || null,
      url,
      trackingId,
    });
    setOpen(false);
  };

  return (
    <>
      <PageHeader
        eyebrow="Divulgação"
        title="Links e códigos"
        description="O código é fácil de falar; o link de afiliada é o que preserva sua comissão."
        action={
          <Button onClick={showCreate}>
            <PlusCircle size={18} /> Novo link
          </Button>
        }
      />
      <aside className="info-banner">
        <Link2 size={20} />
        <div>
          <strong>Como divulgar os IDs da Temu</strong>
          <p>
            Diga “procure o código T001 no link da minha bio”. O T001 é seu
            código curto no WeSell; o clique abre o link afiliado oficial da
            Temu e mantém o rastreamento.
          </p>
        </div>
      </aside>
      <div className="toolbar panel">
        <label className="select-field">
          <span>Plataforma</span>
          <select
            value={platformFilter}
            onChange={(event) => setPlatformFilter(event.target.value)}
          >
            <option value="all">Todas</option>
            {data.platforms.map((platform) => (
              <option value={platform.id} key={platform.id}>
                {platform.name}
              </option>
            ))}
          </select>
        </label>
        <label className="select-field">
          <span>Conteúdo</span>
          <select
            value={contentFilter}
            onChange={(event) => setContentFilter(event.target.value)}
          >
            <option value="all">Todos</option>
            {data.contents.map((content) => (
              <option value={content.id} key={content.id}>
                {content.title}
              </option>
            ))}
          </select>
        </label>
      </div>
      {filtered.length ? (
        <div className="panel responsive-table">
          <table>
            <thead>
              <tr>
                <th>Código</th>
                <th>Produto e conteúdo</th>
                <th>Plataforma</th>
                <th>Validação</th>
                <th>Resultado</th>
                <th>Ações</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((promotion) => {
                const product = data.products.find(
                  (item) => item.id === promotion.productId,
                );
                const platform = data.platforms.find(
                  (item) => item.id === promotion.platformId,
                );
                const content = data.contents.find(
                  (item) => item.id === promotion.contentId,
                );
                const commissions = Array.from(
                  commissionByPromotion.get(promotion.id)?.entries() ?? [],
                );
                return (
                  <tr key={promotion.id}>
                    <td>
                      <strong className="large-code">
                        {product?.shortCode ?? "Sem código"}
                      </strong>
                      <small>{promotion.trackingId || "Sem tracking"}</small>
                    </td>
                    <td>
                      <strong>{product?.name ?? "Produto removido"}</strong>
                      <small>
                        {content?.title ?? "Sem conteúdo vinculado"}
                      </small>
                    </td>
                    <td>
                      {platform ? (
                        <PlatformBadge platform={platform} compact />
                      ) : (
                        "Não encontrada"
                      )}
                    </td>
                    <td>
                      <StatusChip
                        status={promotion.validated ? "ok" : "warning"}
                        label={
                          promotion.validated
                            ? "Código confere"
                            : "Revisar código"
                        }
                      />
                    </td>
                    <td>
                      <strong>{promotion.clicks} cliques</strong>
                      <small>
                        {commissions.length
                          ? commissions
                              .map(([currency, amount]) =>
                                money(amount, currency),
                              )
                              .join(" + ")
                          : "Sem comissão vinculada"}
                      </small>
                    </td>
                    <td>
                      <div className="table-actions">
                        <Button
                          variant="secondary"
                          onClick={() => copy(promotion.url, promotion.id)}
                        >
                          <Copy size={16} />
                          {copied === promotion.id ? "Copiado" : "Copiar"}
                        </Button>
                        <a
                          className="icon-button"
                          href={promotion.url}
                          target="_blank"
                          rel="noreferrer sponsored"
                          aria-label="Abrir link"
                        >
                          <ExternalLink size={17} />
                        </a>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState
          icon={<Link2 size={28} />}
          title="Nenhum link nessa seleção"
          description="Crie um link de divulgação ou altere os filtros."
          action={<Button onClick={showCreate}>Criar primeiro link</Button>}
        />
      )}
      <Modal
        open={open}
        onClose={() => setOpen(false)}
        title="Criar link de divulgação"
        description="Associe o link oficial a um produto e, se quiser, ao conteúdo que vai divulgá-lo."
        footer={
          <>
            <Button variant="secondary" onClick={() => setOpen(false)}>
              Cancelar
            </Button>
            <Button form="promotion-form" type="submit" loading={saving}>
              Criar link
            </Button>
          </>
        }
      >
        <form id="promotion-form" className="form-grid" onSubmit={submit}>
          <Field label="Produto">
            <select
              value={productId}
              onChange={(event) => {
                const next = data.products.find(
                  (item) => item.id === event.target.value,
                );
                setProductId(event.target.value);
                setUrl(next?.affiliateUrl ?? "");
              }}
              required
            >
              {data.products.map((product) => (
                <option value={product.id} key={product.id}>
                  {product.shortCode} · {product.name}
                </option>
              ))}
            </select>
          </Field>
          <Field
            label="Conteúdo"
            helper="Opcional: permite medir qual post vendeu."
          >
            <select
              value={contentId}
              onChange={(event) => setContentId(event.target.value)}
            >
              <option value="">Sem conteúdo</option>
              {data.contents
                .filter((content) => content.productIds.includes(productId))
                .map((content) => (
                  <option value={content.id} key={content.id}>
                    {content.title}
                  </option>
                ))}
            </select>
          </Field>
          <Field label="Link afiliado oficial">
            <input
              type="url"
              value={url}
              onChange={(event) => setUrl(event.target.value)}
              required
            />
          </Field>
          <Field
            label="Tracking / SubID"
            helper="Use o código configurado quando a plataforma não o mostra no link."
          >
            <input
              value={trackingId}
              onChange={(event) => setTrackingId(event.target.value)}
            />
          </Field>
          {validation ? (
            <p
              className={
                validation.valid ? "success-message" : "warning-message"
              }
              role="status"
            >
              {validation.reason}
            </p>
          ) : null}
        </form>
      </Modal>
    </>
  );
}
